import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import gsap from "gsap";
import data from "../data/products.json";
import images from "../assets/products";
import config, { whatsappLink } from "../config";

const products = data.products;
const N = products.length;
const AUTOPLAY_MS = 5200;

/** Offset i mbështjellë: sa hapa larg produktit aktiv, në rangun -3..4. */
function wrap(i, active) {
  let o = (i - active + N) % N;
  if (o > N / 2) o -= N;
  return o;
}

/** Pozicioni në hapësirë për një produkt sipas distancës nga qendra. */
function layout(offset, width) {
  const abs = Math.abs(offset);
  const dir = Math.sign(offset);

  // Hapi horizontal si pjesë e gjerësisë së skenës — kështu përshtatet vetë.
  const unit = width < 560 ? 0.34 : width < 900 ? 0.27 : 0.225;
  const step = width * unit;

  if (abs > 3) {
    return {
      x: dir * width * 0.8,
      y: 0,
      scale: 0.2,
      rotationY: 0,
      opacity: 0,
      blur: 6,
      z: 0,
      hidden: true,
    };
  }

  const scale = [1, 0.66, 0.44, 0.3][abs];
  const opacity = [1, 0.7, 0.34, 0.1][abs];
  const blur = [0, 1.6, 3.2, 4.8][abs];
  const tilt = [0, 26, 34, 38][abs];

  return {
    // Hapat shtypen pak sa më larg shkohet — perspektivë më e besueshme.
    x: offset * step * (1 - abs * 0.07),
    // Lartësi të ndryshme majtas/djathtas që të mos duken si rresht.
    y: abs === 0 ? 0 : (dir > 0 ? 1 : -1) * (10 + abs * 16),
    scale,
    rotationY: -dir * tilt,
    opacity,
    blur,
    z: 30 - abs,
    hidden: false,
  };
}

export default function Hero() {
  const [active, setActive] = useState(0);
  const [shown, setShown] = useState(0);
  const [paused, setPaused] = useState(false);

  const stageRef = useRef(null);
  const itemRefs = useRef([]);
  const copyRef = useRef(null);
  const glowRef = useRef(null);
  const firstRun = useRef(true);
  const touchX = useRef(null);

  const go = useCallback((next) => {
    setActive(((next % N) + N) % N);
  }, []);

  /* ---- pozicionimi i produkteve ---- */
  const place = useCallback(
    (animate) => {
      const stage = stageRef.current;
      if (!stage) return;
      const width = stage.offsetWidth;

      itemRefs.current.forEach((el, i) => {
        if (!el) return;
        const l = layout(wrap(i, active), width);

        const props = {
          x: l.x,
          y: l.y,
          scale: l.scale,
          rotationY: l.rotationY,
          opacity: l.opacity,
          filter: `blur(${l.blur}px)`,
          zIndex: l.z,
        };

        // Produkti që kërcen nga njëra anë në tjetrën nuk duhet të fluturojë
        // nëpër ekran — ai është i padukshëm, prandaj vendoset menjëherë.
        if (!animate || l.hidden) {
          gsap.set(el, props);
        } else {
          gsap.to(el, {
            ...props,
            duration: 1.05,
            ease: "power3.inOut",
            overwrite: "auto",
          });
        }
      });
    },
    [active]
  );

  useLayoutEffect(() => {
    place(!firstRun.current);
  }, [place]);

  /* ---- lëvizja e vazhdueshme: çdo produkt noton me ritmin e vet ---- */
  useLayoutEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      itemRefs.current.forEach((el, i) => {
        const inner = el?.firstElementChild;
        if (!inner) return;

        gsap.to(inner, {
          y: i % 2 === 0 ? -16 : -11,
          rotation: i % 3 === 0 ? 2.4 : -1.8,
          duration: 3.4 + (i % 5) * 0.45,
          delay: i * 0.22,
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true,
        });
      });
    });

    return () => ctx.revert();
  }, []);

  /* ---- hyrja e parë ---- */
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-stagger", {
        y: 30,
        opacity: 0,
        duration: 1,
        stagger: 0.09,
        ease: "power3.out",
        delay: 0.12,
      });
      gsap.from(itemRefs.current.filter(Boolean), {
        opacity: 0,
        scale: 0.8,
        duration: 1.2,
        stagger: { each: 0.06, from: "center" },
        ease: "power3.out",
        delay: 0.3,
      });
    });
    firstRun.current = false;
    return () => ctx.revert();
  }, []);

  /* ---- teksti ndërrohet me shkrirje, ngjyra e sfondit e ndjek ---- */
  useLayoutEffect(() => {
    if (firstRun.current) return;

    const tl = gsap.timeline();
    tl.to(copyRef.current, {
      opacity: 0,
      y: -12,
      duration: 0.28,
      ease: "power2.in",
    })
      .add(() => setShown(active))
      .to(copyRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power3.out",
      });

    if (glowRef.current) {
      gsap.to(glowRef.current, {
        backgroundColor: products[active].accentSoft,
        duration: 1.1,
        ease: "power2.inOut",
      });
    }

    return () => tl.kill();
  }, [active]);

  /* ---- rishtrirja kur ndryshon dritarja ---- */
  useEffect(() => {
    const onResize = () => place(false);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [place]);

  /* ---- rrotullimi automatik ---- */
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setActive((a) => (a + 1) % N), AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [paused, active]);

  /* ---- tastiera ---- */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(active + 1);
      if (e.key === "ArrowLeft") go(active - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, go]);

  const p = products[shown];

  return (
    <Wrap
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <Glow ref={glowRef} style={{ backgroundColor: products[0].accentSoft }} />
      <Grain aria-hidden="true" />

      <Inner>
        <Copy ref={copyRef}>
          <Kicker className="hero-stagger" style={{ color: p.accent }}>
            {p.name}
          </Kicker>
          <Title className="hero-stagger">{p.tagline}</Title>
          <Desc className="hero-stagger">{p.short}</Desc>

          <Actions className="hero-stagger">
            <Primary to={`/produktet/${p.slug}`} style={{ background: p.accent }}>
              Shiko produktin
            </Primary>
            <Secondary
              href={whatsappLink(
                `Përshëndetje! Dua të porosis Aroma Stick ${p.name}.`
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              Porosit në WhatsApp
            </Secondary>
          </Actions>

          <Meta className="hero-stagger">
            <span>{config.price}</span>
            <Dot />
            <span>{config.shipping}</span>
            <Dot />
            <span>{config.bundle}</span>
          </Meta>
        </Copy>

        <Stage
          ref={stageRef}
          onTouchStart={(e) => {
            touchX.current = e.touches[0].clientX;
            setPaused(true);
          }}
          onTouchEnd={(e) => {
            if (touchX.current == null) return;
            const dx = e.changedTouches[0].clientX - touchX.current;
            if (Math.abs(dx) > 45) go(active + (dx < 0 ? 1 : -1));
            touchX.current = null;
            setPaused(false);
          }}
        >
          <Floor />
          {products.map((item, i) => (
            <Item
              key={item.slug}
              ref={(el) => (itemRefs.current[i] = el)}
              onClick={() => (i === active ? null : go(i))}
              $active={i === active}
              aria-hidden={i !== active}
            >
              <Float>
                <img
                  src={images[item.slug]}
                  alt={`Aroma Stick ${item.name}`}
                  draggable="false"
                  loading={i === 0 ? "eager" : "lazy"}
                />
                <Shadow />
              </Float>
            </Item>
          ))}
        </Stage>

        <Dots className="hero-stagger">
          {products.map((item, i) => (
            <DotBtn
              key={item.slug}
              onClick={() => go(i)}
              $on={i === active}
              style={i === active ? { background: item.accent } : undefined}
              aria-label={item.name}
            />
          ))}
        </Dots>
      </Inner>
    </Wrap>
  );
}

/* ----------------------------- styles ----------------------------- */

const Wrap = styled.section`
  position: relative;
  min-height: 100svh;
  display: flex;
  align-items: center;
  padding: 92px 0 48px;
  overflow: hidden;
  isolation: isolate;

  ${({ theme }) => theme.bp.md} {
    padding: 120px 0 64px;
  }
`;

const Glow = styled.div`
  position: absolute;
  z-index: -2;
  top: -24%;
  right: -18%;
  width: 90vw;
  height: 90vw;
  max-width: 1100px;
  max-height: 1100px;
  border-radius: 50%;
  filter: blur(90px);
  opacity: 0.72;
  pointer-events: none;

  ${({ theme }) => theme.bp.md} {
    top: -32%;
    right: -8%;
    width: 60vw;
    height: 60vw;
    filter: blur(120px);
  }
`;

const Grain = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  opacity: 0.4;
  background-image: radial-gradient(
    circle at 1px 1px,
    rgba(28, 27, 24, 0.055) 1px,
    transparent 0
  );
  background-size: 22px 22px;
  mask-image: radial-gradient(ellipse at 50% 40%, #000 20%, transparent 78%);
  -webkit-mask-image: radial-gradient(
    ellipse at 50% 40%,
    #000 20%,
    transparent 78%
  );
`;

const Inner = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.maxWidth};
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  gap: 0;
  grid-template-columns: 1fr;

  ${({ theme }) => theme.bp.md} {
    padding: 0 40px;
    grid-template-columns: minmax(0, 0.92fr) minmax(0, 1.08fr);
    align-items: center;
    gap: 24px;
  }
`;

const Copy = styled.div`
  order: 2;
  text-align: center;

  ${({ theme }) => theme.bp.md} {
    order: 1;
    text-align: left;
  }
`;

const Kicker = styled.span`
  display: block;
  font-size: ${({ theme }) => theme.size.xs};
  text-transform: uppercase;
  letter-spacing: 0.26em;
  font-weight: 600;
  margin-bottom: 14px;
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.size.h1};
  max-width: 16ch;
  margin: 0 auto 20px;

  ${({ theme }) => theme.bp.md} {
    margin: 0 0 20px;
  }
`;

const Desc = styled.p`
  font-size: ${({ theme }) => theme.size.lg};
  color: ${({ theme }) => theme.colors.inkSoft};
  max-width: 44ch;
  margin: 0 auto 32px;

  ${({ theme }) => theme.bp.md} {
    margin: 0 0 36px;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;

  ${({ theme }) => theme.bp.md} {
    justify-content: flex-start;
  }
`;

const btn = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 15px 30px;
  border-radius: 999px;
  font-size: 0.93rem;
  font-weight: 500;
  transition: transform 0.35s cubic-bezier(0.22,1,0.36,1), box-shadow 0.35s ease, border-color 0.3s ease;
  &:hover { transform: translateY(-2px); }
`;

const Primary = styled(Link)`
  ${btn};
  color: #fff;
  box-shadow: 0 10px 26px -10px rgba(28, 27, 24, 0.5);
`;

const Secondary = styled.a`
  ${btn};
  border: 1px solid ${({ theme }) => theme.colors.line};
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(8px);

  &:hover {
    border-color: ${({ theme }) => theme.colors.ink};
  }
`;

const Meta = styled.div`
  margin-top: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  font-size: ${({ theme }) => theme.size.sm};
  color: ${({ theme }) => theme.colors.inkSoft};

  ${({ theme }) => theme.bp.md} {
    justify-content: flex-start;
  }
`;

const Dot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.inkFaint};
`;

const Stage = styled.div`
  order: 1;
  position: relative;
  width: 100%;
  height: 34svh;
  min-height: 230px;
  max-height: 290px;
  margin-bottom: 26px;
  perspective: 1400px;
  transform-style: preserve-3d;
  touch-action: pan-y;

  ${({ theme }) => theme.bp.md} {
    order: 2;
    height: 68svh;
    min-height: 420px;
    max-height: 640px;
    margin-bottom: 0;
  }
`;

const Floor = styled.div`
  position: absolute;
  left: 50%;
  bottom: 8%;
  width: 70%;
  height: 56px;
  transform: translateX(-50%);
  background: radial-gradient(
    ellipse at center,
    rgba(28, 27, 24, 0.13),
    transparent 68%
  );
  filter: blur(10px);
  pointer-events: none;
`;

const Item = styled.div`
  position: absolute;
  inset: 0;
  margin: auto;
  width: 46%;
  max-width: 230px;
  height: 78%;
  transform-style: preserve-3d;
  will-change: transform, opacity, filter;
  cursor: ${({ $active }) => ($active ? "default" : "pointer")};
  user-select: none;
`;

/* inset:0 i jep kutisë përmasa të sigurta nga prindi. Me width/height 100%
   brenda një grid-i, rreshti matet sipas imazhit dhe ai del jashtë skenës. */
/* inset:0 i jep kutisë përmasa të sigurta nga prindi. Brenda një grid-i,
   rreshti matet sipas imazhit dhe ai del jashtë skenës — prandaj edhe
   vetë imazhi pozicionohet absolutisht me object-fit. Kështu çdo PNG,
   pavarësisht përmasave, qëndron brenda skenës. */
const Float = styled.div`
  position: absolute;
  inset: 0;
  will-change: transform;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: drop-shadow(0 28px 38px rgba(28, 27, 24, 0.26));
    pointer-events: none;
  }
`;

const Shadow = styled.span`
  position: absolute;
  left: 50%;
  bottom: -10px;
  width: 58%;
  height: 20px;
  transform: translateX(-50%);
  background: radial-gradient(
    ellipse at center,
    rgba(28, 27, 24, 0.22),
    transparent 70%
  );
  filter: blur(7px);
  pointer-events: none;
`;

const Dots = styled.div`
  order: 3;
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  gap: 9px;
  margin-top: 26px;

  ${({ theme }) => theme.bp.md} {
    margin-top: 10px;
  }
`;

const DotBtn = styled.button`
  width: ${({ $on }) => ($on ? "26px" : "8px")};
  height: 8px;
  border-radius: 999px;
  background: ${({ theme, $on }) =>
    $on ? theme.colors.ink : theme.colors.line};
  transition: width 0.5s ${({ theme }) => theme.ease}, background 0.5s ease;

  &:hover {
    background: ${({ theme, $on }) =>
      $on ? undefined : theme.colors.inkFaint};
  }
`;
